<template>
  <div class="quillWrapper">
    <slot name="toolbar"></slot>
    <div :id="id" ref="quillContainer"></div>
    <input
      v-if="useCustomImageHandler"
      id="file-upload"
      ref="fileInput"
      type="file"
      accept="image/*"
      style="display:none;"
      @change="emitImageInfo($event)"
    />
  </div>
</template>

<script>
import Quill from "quill";
import defaultToolbar from "./helpers/default-toolbar";
import oldApi from "./helpers/old-api";
import mergeDeep from "./helpers/merge-deep";
import MarkdownShortcuts from "./helpers/markdown-shortcuts";
import Table from "./modules/table"
import CustomImage from "./formats/image"
import BetterTable from "./modules/quill-better-table/quill-better-table"
import TPM from "./modules/tpm"
import './css/custom.css';

const AlignStyle = Quill.import('attributors/style/align');
Quill.register('formats/align', AlignStyle, true);
const FontStyle = Quill.import('attributors/style/font');
Quill.register('formats/font', FontStyle, true);
const SizeStyle = Quill.import('attributors/style/size');
Quill.register('formats/size', SizeStyle, true);

Quill.register('formats/image', CustomImage, true)

Quill.register('modules/table', Table, true)
Quill.register('modules/better-table', BetterTable, true)
Quill.register('modules/tpm', TPM, true)

const Delta = Quill.import('delta')

export default {
  name: "VueEditor",
  mixins: [oldApi],
  props: {
    id: {
      type: String,
      default: "quill-container"
    },
    placeholder: {
      type: String,
      default: ""
    },
    value: {
      type: String,
      default: ""
    },
    disabled: {
      type: Boolean
    },
    editorToolbar: {
      type: Object,
      default: () => []
    },
    editorOptions: {
      type: Object,
      required: false,
      default: () => ({})
    },
    useCustomImageHandler: {
      type: Boolean,
      default: false
    },
    useMarkdownShortcuts: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    quill: null
  }),

  watch: {
    value(val) {
      if (val != this.quill.root.innerHTML && !this.quill.hasFocus()) {
        this.quill.root.innerHTML = val;
      }
    },
    disabled(status) {
      this.quill.enable(!status);
    }
  },

  mounted() {
    this.registerCustomModules(Quill);
    this.registerPrototypes();
    this.initializeEditor();
  },

  beforeDestroy() {
    this.quill = null;
    delete this.quill;
  },

  methods: {
    initializeEditor() {
      this.setupQuillEditor();
      this.checkForCustomImageHandler();
      this.setupSaltoPaginaHandler();
      this.handleInitialContent();
      this.registerEditorEventListeners();
      this.$emit("ready", this.quill);
    },

    setupSaltoPaginaHandler() {
      const toolbar = this.quill.getModule("toolbar");
      toolbar.addHandler("saltoPagina", (valor) => {
        const range = this.quill.getSelection(true)
        if (range == null) return
        this.quill.updateContents( new Delta().retain(range.index).delete(range.length).insert({ ["saltoPagina"]: valor }), Quill.sources.USER);
        this.quill.setSelection(range.index + 1 + (valor?valor.length:0), Quill.sources.SILENT);
      });
    },

    setupQuillEditor() {
      const editorConfig = {
        debug: false,
        modules: this.setModules(),
        theme: "snow",
        placeholder: this.placeholder ? this.placeholder : "",
        readOnly: this.disabled ? this.disabled : false
      };

      this.prepareEditorConfig(editorConfig);
      this.quill = new Quill(this.$refs.quillContainer, editorConfig);
    },

    setModules() {
      const modules = {
        toolbar: this.editorToolbar ? this.editorToolbar : defaultToolbar,
        table: false,
        'better-table': {
          operationMenu: {
            color: {
              colors: ['rgb(35, 53, 91)', 'white', '#ff7272', 'black', '#777', '#ddd'],  // colors you need in operationMenu, ['white', 'red', 'yellow', 'blue'] as default
              text: 'Color de fondo'  // subtitle, 'Background Colors' as default
            },
            borderColor: {
              colors: ['rgb(35, 53, 91)', 'white', '#ff7272', 'black', '#777', '#ddd', 'none'],  // colors you need in operationMenu, ['white', 'red', 'yellow', 'blue'] as default
              text: 'Color de borde'  // subtitle, 'Background Colors' as default
            },
            items: {
              insertColumnRight: {
                text: 'Insertar columna a la dcha'
              },
              insertColumnLeft: {
                text: 'Insertar columna a la izda'
              },
              insertRowUp: {
                text: 'Insertar fila arriba'
              },
              insertRowDown: {
                text: 'Insertar fila abajo',
                dividingAfter: true,
              },
              mergeCells: {
                text: 'Fusionar celdas'
              },
              unmergeCells: {
                text: 'Separar celdas',
                dividingAfter: true,
              },
              deleteColumn: {
                text: 'Borrar columna'
              },
              deleteRow: {
                text: 'Borrar fila'
              },
              deleteTable: {
                text: 'Borrar tabla'
              },
              centrarTabla: {
                text: 'Centrar tabla',
                dividingBefore: true,
                iconSrc: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAIAAAACACAYAAADDPmHLAAAACXBIWXMAAAOwAAADsAEnxA+tAAAAGXRFWHRTb2Z0d2FyZQB3d3cuaW5rc2NhcGUub3Jnm+48GgAABVVJREFUeJzt3E9oHGUYBvDn2w1pDxWsWCoiQQ+VopUKoZAw45KzIq1CPXiOoujFk4rtyVp68yAiGi9e7cH6r+ew7mxCJSpSpaigBEShEhULqWRnXw/VhmZHJvv9mTflfX7HCfPlI/swO7uZ9wGIiIiIyBynvQEtR9/4+gG02vMA0Gq5hQ+eve+i9p40mAzAtRe/dcEBuwFAgKutVuuIxRC0tDegwTn31H8vPgA4YLcMZV5zT1pMBgDO3TF6UO5sfiP6bAaArmMAjGMAjGMAjGMAjGMAjGMAjGMAjGMAjJuItVCn07mnLMuDzrk9sdZMpbxy+a72nn0jx/I8P660pW0TkSvtdvtSt9v9McZ6wQHI8/xREXm1LMvDACAi4btKbLC2iq0BGKytzorIrNKWxlKWJbIs+0pETvT7/U9D1gp6C8iy7LSIfATgcMg65OVB59wneZ6fClnEOwBZlj0D4OWQX07hROSVPM+f9j3fKwBzc3O3Ajjt+0spLhE58+9rMjavAAwGg8cB7PU5l5LYu7GxcdTnRK+bwOFwOO3cyMNEvw6Hw4eWlpZ+8FmzSceefPt9ADfc8e+amj5bFMUTSlvatk6nc6Asy88A7N/yoyMA3ht3Pa8rgHPu9orD/Zvhxb/Zdbvd7wEUFT/aV3Gslu9NYNWzhKXnWjS+qr+11/Od/CbQOAbAOAbAOAbAOAbAOAbAOAbAOAbAOAbAOAbAOAbAON8AVD331Q7ZCI2l6m/t9SyeVwBE5LeKw1mn0zngsx5tX57n9wLIKn502Wc934dCP684tr8sy++yrGpvO8vfqyvYNTW99djxLMt2/BOt//fQrXPugs96XleAycnJcwB+9zmXklhbX1//0OdErwAsLi7+4Zx7yedcSuLFlZWVP31O9P4U0Ov13nHOveZ7PkUhInKqKIp3fRcI+hjY6/VOAHgEwJch69D4ROQL59zD/X7/ZMg6wZNBRVGcB3B+Zmbm7omJiYMAbgldM7WJ26ZeADC75diSc+51pS2N46/BYHBpeXn5pxiLmewJPPbmxZGnggGcPffcoR3/VHBs/CbQOAbAOAbAuGj9AKGa7BfQ7AeIPd8fSj0AGv0C2v0AMef7Q6m+BRjvF4gy3x9KLQDsF7gmdL4/lEoA2C9wo5D5/lAqAWC/wAjv+f5QKjeB2v0Cmv0Asef7Q6lcASz3C8Se7w+ldRNovV8g2nx/KH4TaBwDYBwDYBwDYBwDYBwDYBwDYBwDYBwDYBwDYBwDYJxWAKz3C0Sb7w+lEgDL/QKx5/tDaT0UqtovoNkPEHu+P5TKFYD9AiO85/tDqQSA/QIjvOf7Q6l9CmC/AIAI8/2hVD8GWu4XiDXfH0p9MkijX0C5HyDqfH8o9gNsYj8A2cMAGMcAGNfYTWCT8/91NPsB6jTdH5A8ABrz/3W0+wHqNNkfkPQtwPj8f6hG+gOSBYDz/3Gk7g9IEgDO/8eVsj8gSQA4/x9dsv6AJDeB2vP/dTT7Aeo03R+Q5Apgef4/VNP9AaluAq3P/4dqrD+A3wQaxwAYxwAYxwAYxwAYxwAYxwAYxwAYxwAYxwAYxwAYlyoA1uf/QzXWH5AkAJbn/0M13R+Q6qFQ1fn/Opr9AHWa7g9IcgXg/H90yfoDkgSA8//RJesPSPYpgPP/USTvD0j6MdDy/H+opvoDkk8Gacz/11HuB6jTaH8A+wE2sR+A7GEAjGMAjGMAjGMAjGMAjGMAjGMAjGMAjDMZAIH7ZeSgw88KW1FnMgCtllsQ4OrmEbdeDp1aY7cmk/8LAIDH3vr2kAxlHgBKwcLHz9//jfaeiIiIiIga8Q/6mFu70rq/2QAAAABJRU5ErkJggg==',
                handler: function () {
                  if (this.table.style.margin) {
                    this.table.style.margin = '';
                    this.tableColumnTool.domNode.style.justifyContent = '';
                  } else {
                    this.table.style.margin = '0 auto';
                    this.tableColumnTool.domNode.style.justifyContent = 'center';
                  }
                }
              },
            }
          }
        },
        keyboard: {
          bindings: BetterTable.keyboardBindings
        }
      };
      if (this.useMarkdownShortcuts) {
        Quill.register("modules/markdownShortcuts", MarkdownShortcuts, true);
        modules["markdownShortcuts"] = {};
      }
      return modules;
    },

    prepareEditorConfig(editorConfig) {
      if (
        Object.keys(this.editorOptions).length > 0 &&
        this.editorOptions.constructor === Object
      ) {
        if (
          this.editorOptions.modules &&
          typeof this.editorOptions.modules.toolbar !== "undefined"
        ) {
          // We don't want to merge default toolbar with provided toolbar.
          delete editorConfig.modules.toolbar;
        }

        mergeDeep(editorConfig, this.editorOptions);
      }
    },

    registerPrototypes() {
      Quill.prototype.getHTML = function() {
        return this.container.querySelector(".ql-editor").innerHTML;
      };
      Quill.prototype.getWordCount = function() {
        return this.container.querySelector(".ql-editor").innerText.length;
      };
    },

    registerEditorEventListeners() {
      this.quill.on("text-change", this.handleTextChange);
      this.quill.on("selection-change", this.handleSelectionChange);
      this.listenForEditorEvent("text-change");
      this.listenForEditorEvent("selection-change");
      this.listenForEditorEvent("editor-change");
    },

    listenForEditorEvent(type) {
      this.quill.on(type, (...args) => {
        this.$emit(type, ...args);
      });
    },

    handleInitialContent() {
      if (this.value) this.quill.root.innerHTML = this.value; // Set initial editor content
    },

    handleSelectionChange(range, oldRange) {
      if (!range && oldRange) this.$emit("blur", this.quill);
      else if (range && !oldRange) this.$emit("focus", this.quill);
    },

    handleTextChange(delta, oldContents) {
      const editorContent =
        this.quill.getHTML() === "<p><br></p>" ? "" : this.quill.getHTML();
      this.$emit("input", editorContent);

      if (this.useCustomImageHandler)
        this.handleImageRemoved(delta, oldContents);
    },

    handleImageRemoved(delta, oldContents) {
      const currrentContents = this.quill.getContents();
      const deletedContents = currrentContents.diff(oldContents);
      const operations = deletedContents.ops;

      operations.map(operation => {
        if (operation.insert && operation.insert.hasOwnProperty("image")) {
          const { image } = operation.insert;
          this.$emit("image-removed", image);
        }
      });
    },
    checkForCustomImageHandler() {
      this.useCustomImageHandler === true ? this.setupCustomImageHandler() : "";
    },

    setupCustomImageHandler() {
      const toolbar = this.quill.getModule("toolbar");
      toolbar.addHandler("image", this.customImageHandler);
    },

    customImageHandler() {
      this.$refs.fileInput.click();
    },

    emitImageInfo($event) {
      const resetUploader = function() {
        var uploader = document.getElementById("file-upload");
        uploader.value = "";
      };
      const file = $event.target.files[0];
      const Editor = this.quill;
      const range = Editor.getSelection();
      const cursorLocation = range.index;
      this.$emit("image-added", file, Editor, cursorLocation, resetUploader);
    }
  }
};
</script>

<style src="quill/dist/quill.snow.css"></style>
<style src="./assets/vue2-editor.css"></style>
